import Auth from './auth';
import http from './http';
import EntityAPI from './entity';
import { storeUserWithTokenClaims } from '../utils/authStorage';
import { ROLE } from '../utils/common';

const ADMIN_MEMBERSHIP_ROLES: ReadonlySet<string> = new Set([
    ROLE.SUPER_ADMIN,
    ROLE.ORGANIZATION_ADMINISTRATOR,
    ROLE.ADMINISTRATOR,
]);

const isAdminMembershipRole = (role: string | undefined | null): boolean =>
    !!role && ADMIN_MEMBERSHIP_ROLES.has(role);

const getActiveMemberships = (userDetail: any): AccountMembership[] => {
    const list = userDetail?.user?.memberships ?? [];
    if (!Array.isArray(list)) return [];
    return list.filter((m: any) => m.isActive !== false);
};

export interface AccountMembership {
    entityId: any;
    role: string;
    isActive: boolean;
    platformContext?: string;
    entityName?: string; // Populated by backend after switch
}

export interface EntityNameMap {
    [entityId: string]: string;
}

/**
 * Get admin memberships for the current user
 * Returns memberships where user has admin privileges
 */
export const getAdminMemberships = (userDetail: any): AccountMembership[] => {
    const active = getActiveMemberships(userDetail);
    if (active.length < 2) {
        return [];
    }
    return active.filter((membership: any) => isAdminMembershipRole(membership.role));
};

/** Active memberships eligible for Aptive Index account switching (requires 2+ orgs). */
export const getSwitchableMemberships = (userDetail: any): AccountMembership[] => {
    const active = getActiveMemberships(userDetail);
    if (active.length < 2) return [];

    const hasAnyAdminMembership = active.some((m: any) => isAdminMembershipRole(m.role));
    const switchable = hasAnyAdminMembership
        ? active.filter((m: any) => isAdminMembershipRole(m.role))
        : active;

    return switchable.length >= 2 ? switchable : [];
};

export const hasMultipleSwitchableAccounts = (userDetail: any): boolean => {
    return getSwitchableMemberships(userDetail).length > 1;
};

/**
 * Get switchable memberships for Aria standalone users
 * Includes aria_chat memberships even if user is not an admin
 */
export const getAriaSwitchableMemberships = (userDetail: any): AccountMembership[] => {
    const active = getActiveMemberships(userDetail);
    if (active.length < 2) {
        return [];
    }

    const hasAnyAdminMembership = active.some((m: any) => isAdminMembershipRole(m.role));

    // For Aria standalone, show admin memberships and aria_chat/both memberships
    return active.filter((membership: any) => {
        const isAdmin = isAdminMembershipRole(membership.role);
        const isAriaMembership =
            membership.platformContext === 'aria_chat' ||
            membership.platformContext === 'both';

        if (hasAnyAdminMembership && membership.role === ROLE.STANDARD_USER && !isAriaMembership) {
            return false;
        }

        return isAdmin || isAriaMembership;
    });
};

/**
 * Check if user is admin in multiple entities
 */
export const isAdminInMultipleEntities = (userDetail: any): boolean => {
    const adminMemberships = getAdminMemberships(userDetail);
    return adminMemberships.length > 1;
};

/**
 * Check if user has multiple switchable accounts in Aria standalone
 */
export const hasMultipleAriaSwitchableAccounts = (userDetail: any): boolean => {
    const switchableMemberships = getAriaSwitchableMemberships(userDetail);
    return switchableMemberships.length > 1;
};

/**
 * Fetch entity names for a list of memberships
 * Uses entityName from membership if available (populated by backend after switch)
 * Falls back to API call, then to entityId if permission denied
 */
export const fetchEntityNames = async (memberships: AccountMembership[]): Promise<EntityNameMap> => {
    try {
        const namePromises = memberships.map(async (membership) => {
            const entityId = membership.entityId.toString();
            
            // First, check if entityName is already in the membership (from backend)
            if (membership.entityName) {
                return { id: entityId, name: membership.entityName };
            }
            
            // Otherwise, try to fetch from API
            try {
                const entityDetails = await EntityAPI.getEntityDetails(entityId);
                return { id: entityId, name: entityDetails.name };
            } catch (error: any) {
                // If permission denied or any error, fall back to using entityId as the name
                console.warn(`Could not fetch entity name for ${entityId}, using ID as fallback`);
                return { id: entityId, name: entityId };
            }
        });

        const results = await Promise.all(namePromises);
        const nameMap: EntityNameMap = {};
        results.forEach(result => {
            nameMap[result.id] = result.name;
        });
        return nameMap;
    } catch (error) {
        console.error('Error fetching entity names:', error);
        // Return a map with entityIds or entityNames as fallback
        const fallbackMap: EntityNameMap = {};
        memberships.forEach(membership => {
            const id = membership.entityId.toString();
            fallbackMap[id] = membership.entityName || id;
        });
        return fallbackMap;
    }
};

/**
 * Check if we're in Aria standalone context
 */
export const isAriaStandaloneContext = (): boolean => {
    const hostname = window.location.hostname;
    const path = window.location.pathname;
    
    // Check if on Aria subdomain
    const isAriaSubdomain = hostname.includes('ariachat.ai');
    
    // Check if on /aria path
    const isAriaPath = path.startsWith('/aria');
    
    return isAriaSubdomain || isAriaPath;
};

/**
 * Get the appropriate redirect URL after account switch
 * For Aria standalone, return to Aria base URL
 * For main platform, return to root
 */
export const getPostSwitchRedirectUrl = (): string => {
    if (isAriaStandaloneContext()) {
        // Import dynamically to avoid circular dependencies
        const { getAriaAppBaseUrl } = require('../utils/ariaUrls');
        return getAriaAppBaseUrl();
    }
    return '/';
};

/**
 * Switch to a different entity/account
 * @param entityId - The entity ID to switch to
 * @returns Promise that resolves when switch is complete
 */
export const switchToAccount = async (entityId: string): Promise<void> => {
    try {
        const response = await Auth.switchEntity(entityId);

        const newUserData = {
            token: response.token,
            user: response.user
        };
        storeUserWithTokenClaims(newUserData);

        if (response.user?.defaultEntityId) {
            persistDefaultEntityId(response.user.defaultEntityId.toString());
        }

        if (response.user.platformContext) {
            localStorage.setItem('platformContext', response.user.platformContext);
        }

        http.setAuthToken(response.token);
        
        // Get appropriate redirect URL based on context
        const redirectUrl = getPostSwitchRedirectUrl();
        window.location.href = redirectUrl;
    } catch (error: any) {
        console.error('Failed to switch account:', error);
        throw new Error(error?.response?.data?.message || 'Failed to switch account. Please try again.');
    }
};

/**
 * Get the current entity ID from user data
 */
export const getCurrentEntityId = (userDetail: any): string => {
    return (
        userDetail?.user?.currentEntityId?.toString?.() ||
        userDetail?.user?.entityId?.toString?.() ||
        ''
    );
};

/** Home org used on login (stored on the user document, not the active session). */
export const getDefaultEntityId = (userDetail: any): string => {
    return (
        userDetail?.user?.defaultEntityId?.toString?.() ||
        userDetail?.user?.entityId?.toString?.() ||
        ''
    );
};

export const persistDefaultEntityId = (defaultEntityId: string): void => {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed?.user) return;
        parsed.user.defaultEntityId = defaultEntityId;
        localStorage.setItem('user', JSON.stringify(parsed));
    } catch (error) {
        console.warn('Could not persist default entity id', error);
    }
};

export const setDefaultAccount = async (entityId: string): Promise<string> => {
    const response = await Auth.setDefaultEntity(entityId);
    const defaultEntityId = response.defaultEntityId?.toString?.() ?? entityId;
    persistDefaultEntityId(defaultEntityId);
    return defaultEntityId;
};
